// Tremor BarChart [v1.0.0]
/* eslint-disable @typescript-eslint/no-explicit-any */

"use client";

import React from "react";
import {
  Bar,
  CartesianGrid,
  Label,
  BarChart as RechartsBarChart,
  Legend as RechartsLegend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  AvailableChartColors,
  constructCategoryColors,
  getColorClassName,
  type AvailableChartColorsKeys,
  calculateDynamicMargins,
  measureTextWidth,
} from "@/lib/chartUtils";
import {
  calculateNiceYAxisDomain,
  generateCustomTicks,
  valueToPercent,
} from "@/lib/chartUtils";
import { cn } from "@/lib/utils";
import { ChartLegend } from "./components/ChartLegend";
import { ChartTooltip, type TooltipProps } from "./components/ChartTooltip";
import { createCustomTick } from "./components/CustomTick";

//#region Shape

interface RechartsBarShapeProps {
  fillOpacity?: number;
  name?: string;
  payload?: Record<string, unknown>;
  value?: number;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

interface RechartsTooltipPayloadItem {
  dataKey?: string | number;
  value?: number;
  payload?: Record<string, unknown>;
  type?: string;
}

interface BarClickData {
  payload?: Record<string, unknown>;
  value?: number;
  tooltipPayload?: Array<{
    dataKey?: string | number;
    value?: number;
    payload?: Record<string, unknown>;
    type?: string;
  }>;
}

const renderShape = (props: RechartsBarShapeProps, layout: string) => {
  const { fillOpacity } = props;
  let { x = 0, width = 0, y = 0, height = 0 } = props;

  if (layout === "horizontal" && height < 0) {
    y = (y ?? 0) + height;
    height = Math.abs(height); // height must be a positive number
  } else if (layout === "vertical" && width < 0) {
    x = (x ?? 0) + width;
    width = Math.abs(width); // width must be a positive number
  }

  return (
    <rect
      x={x ?? 0}
      y={y ?? 0}
      width={width ?? 0}
      height={height ?? 0}
      opacity={fillOpacity ?? 1}
    />
  );
};

//#region Legend - Using shared component

//#region Tooltip - Using shared component

//#region BarChart

type BaseEventProps = {
  eventType: "category" | "bar";
  categoryClicked: string;
  [key: string]: number | string;
};

type BarChartEventProps = BaseEventProps | null | undefined;

interface BarChartProps extends React.HTMLAttributes<HTMLDivElement> {
  data: Record<string, any>[];
  index: string;
  categories: string[];
  colors?: AvailableChartColorsKeys[];
  valueFormatter?: (value: number) => string;
  startEndOnly?: boolean;
  showXAxis?: boolean;
  showYAxis?: boolean;
  showGridLines?: boolean;
  yAxisWidth?: number;
  intervalType?: "preserveStartEnd" | "equidistantPreserveStart" | 0;
  tickCount?: number;
  showTooltip?: boolean;
  showLegend?: boolean;
  autoMinValue?: boolean;
  minValue?: number;
  maxValue?: number;
  allowDecimals?: boolean;
  onValueChange?: (value: BarChartEventProps) => void;
  enableLegendSlider?: boolean;
  tickGap?: number;
  barCategoryGap?: string | number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  layout?: "vertical" | "horizontal";
  type?: "default" | "stacked" | "percent";
  legendPosition?: "left" | "center" | "right";
  tooltipCallback?: (tooltipCallbackContent: TooltipProps) => void;
  customTooltip?: React.ComponentType<TooltipProps>;
  chartMargin?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };
  sharedTooltip?: boolean;
}

const BarChart = React.forwardRef<HTMLDivElement, BarChartProps>(
  (props, forwardedRef) => {
    const {
      data = [],
      categories = [],
      index,
      colors = AvailableChartColors,
      valueFormatter = (value: number) => value.toString(),
      startEndOnly = false,
      showXAxis = true,
      showYAxis = true,
      showGridLines = true,
      yAxisWidth = 20,
      showTooltip = true,
      showLegend = true,
      autoMinValue = false,
      minValue,
      maxValue,
      allowDecimals = true,
      className,
      onValueChange,
      enableLegendSlider = false,
      barCategoryGap,
      tickGap = 2,
      tickCount,
      xAxisLabel,
      yAxisLabel,
      layout = "horizontal",
      type = "default",
      legendPosition = "right",
      tooltipCallback,
      customTooltip,
      chartMargin,
      sharedTooltip = true,
      ...other
    } = props;
    const CustomTooltip = customTooltip;
    const paddingValue =
      (!showXAxis && !showYAxis) || (startEndOnly && !showYAxis) ? 0 : 20;
    const [legendHeight, setLegendHeight] = React.useState(60);
    const categoryColors = constructCategoryColors(categories, colors);
    const [shouldRotateTicks, setShouldRotateTicks] = React.useState(
      data?.length > 21 ? true : false,
    );
    const containerRef = React.useRef<HTMLDivElement>(null);

    // Calculate if labels fit horizontally based on ACTUAL rendered ticks from Recharts
    React.useEffect(() => {
      if (layout === "vertical") {
        setShouldRotateTicks(false); // No rotation needed for vertical layout
        return;
      }

      const calculateRotation = () => {
        if (!containerRef.current || !data?.length) {
          setShouldRotateTicks(false);
          return;
        }

        // Wait for DOM to be fully rendered
        // Use double RAF to ensure Recharts has finished rendering
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const svg = containerRef.current?.querySelector("svg");
            if (!svg) {
              setShouldRotateTicks(true);
              return;
            }

            // Find all x-axis tick text elements that Recharts actually rendered
            const xAxisGroup = svg.querySelector(".recharts-xAxis");
            if (!xAxisGroup) {
              setShouldRotateTicks(true);
              return;
            }

            const tickTexts = Array.from(xAxisGroup.querySelectorAll("text"));

            if (tickTexts.length === 0) {
              setShouldRotateTicks(true);
              return;
            }

            // Get the actual positions and widths of visible ticks
            const tickInfo = tickTexts
              .map((textEl) => {
                const transform = textEl.parentElement?.getAttribute("transform");
                if (!transform) return null;

                // Extract x coordinate from transform="translate(x, y)"
                const match = transform.match(/translate\(([^,]+),/);
                const x = match ? parseFloat(match[1]) : null;
                const text = textEl.textContent || "";

                if (x === null || text === "") return null;

                // Measure the actual text width
                const textWidth = measureTextWidth(text, 12);

                return { x, text, textWidth };
              })
              .filter(
                (info): info is { x: number; text: string; textWidth: number } =>
                  info !== null,
              )
              .sort((a, b) => a.x - b.x); // Sort by x position

            if (tickInfo.length < 2) {
              setShouldRotateTicks(true);
              return;
            }

            // Calculate actual spacing between adjacent visible ticks
            const spacings: number[] = [];
            for (let i = 0; i < tickInfo.length - 1; i++) {
              spacings.push(tickInfo[i + 1].x - tickInfo[i].x);
            }

            const minSpacing = Math.min(...spacings);
            const maxLabelWidth = Math.max(...tickInfo.map((t) => t.textWidth));

            // Labels can fit horizontally if minimum spacing is enough for the widest label
            // Add 5px buffer for visual clarity
            const canFit = minSpacing >= maxLabelWidth + 5;

            setShouldRotateTicks(!canFit);
          });
        });
      };

      calculateRotation();

      // Recalculate when container resizes (e.g. draggable panels)
      const container = containerRef.current;
      if (!container) return;

      const resizeObserver = new ResizeObserver(() => calculateRotation());
      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }, [data, index, chartMargin, xAxisLabel, yAxisLabel, layout, tickGap]);

    // Handle ref forwarding - use containerRef for measurements
    React.useEffect(() => {
      if (containerRef.current && forwardedRef) {
        if (typeof forwardedRef === "function") {
          forwardedRef(containerRef.current);
        } else {
          forwardedRef.current = containerRef.current;
        }
      }
    }, [forwardedRef]);

    const niceYAxisDomain = calculateNiceYAxisDomain(
      data,
      categories,
      autoMinValue,
      minValue,
      maxValue,
      5,
    );
    const customTicks = generateCustomTicks(niceYAxisDomain, 5, allowDecimals);
    const hasOnValueChange = !!onValueChange;
    const stacked = type === "stacked" || type === "percent";

    const prevActiveRef = React.useRef<boolean | undefined>(undefined);
    const prevLabelRef = React.useRef<string | undefined>(undefined);

    // Create tick component with rotation state
    const CustomTickComponent = React.useMemo(
      () => createCustomTick(shouldRotateTicks),
      [shouldRotateTicks],
    );

    function onBarClick(data: BarClickData, _: unknown, event: React.MouseEvent) {
      event.stopPropagation();
      if (!onValueChange) return;
      const dataKey = data.tooltipPayload?.[0]?.dataKey;
      const categoryClicked =
        typeof dataKey === "string" ? dataKey : String(dataKey ?? "");

      // Simply call the handler without any visual state changes
      onValueChange({
        eventType: "bar",
        categoryClicked,
        ...(data.payload ?? {}),
      });
    }

    function onCategoryClick(dataKey: string) {
      if (!hasOnValueChange) return;
      // Simply call the handler without any visual state changes
      onValueChange?.({
        eventType: "category",
        categoryClicked: dataKey,
      });
    }

    const margins = calculateDynamicMargins(
      data,
      index,
      {
        ...chartMargin,
        bottom: !shouldRotateTicks ? 0 : undefined,
        left: !shouldRotateTicks ? 0 : undefined,
      },
      xAxisLabel,
      yAxisLabel,
      0,
      0.5,
    );

    return (
      <div
        ref={containerRef}
        className={cn("h-80 w-full", className)}
        tremor-id="tremor-raw"
        {...other}
      >
        <ResponsiveContainer className="overflow-visible">
          <RechartsBarChart
            data={data}
            onClick={undefined}
            margin={margins}
            className="overflow-visible [&>svg]:overflow-visible"
            stackOffset={type === "percent" ? "expand" : undefined}
            layout={layout}
            barCategoryGap={barCategoryGap}
          >
            {showGridLines ? (
              <CartesianGrid
                className={cn("stroke-gray-200 stroke-1 dark:stroke-gray-800")}
                horizontal={layout !== "vertical"}
                vertical={layout === "vertical"}
              />
            ) : null}
            <XAxis
              hide={!showXAxis}
              tick={layout !== "vertical" ? CustomTickComponent : undefined}
              fill=""
              stroke=""
              className={cn(
                // base
                "text-xs",
                // text fill
                "fill-gray-500 dark:fill-gray-500",
                { "mt-4": layout !== "vertical" },
              )}
              tickLine={false}
              axisLine={false}
              minTickGap={tickGap}
              tickMargin={layout !== "vertical" ? 20 : 10}
              {...(layout !== "vertical"
                ? {
                    padding: {
                      left: paddingValue,
                      right: paddingValue,
                    },
                    dataKey: index,
                    interval: 0,
                    tickCount: tickCount,
                  }
                : {
                    type: "number",
                    domain: niceYAxisDomain,
                    ticks: customTicks,
                    tickFormatter: type === "percent" ? valueToPercent : valueFormatter,
                    allowDecimals: allowDecimals,
                  })}
            >
              {xAxisLabel && (
                <Label
                  position="insideBottom"
                  offset={-20}
                  className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
                >
                  {xAxisLabel}
                </Label>
              )}
            </XAxis>
            <YAxis
              width={yAxisWidth}
              hide={!showYAxis}
              axisLine={false}
              tickLine={false}
              fill=""
              stroke=""
              className={cn(
                // base
                "text-xs",
                // text fill
                "fill-gray-500 dark:fill-gray-500",
              )}
              tick={{
                transform: layout !== "vertical" ? "translate(-3, 0)" : "translate(0, 0)",
              }}
              {...(layout !== "vertical"
                ? {
                    type: "number",
                    domain: niceYAxisDomain,
                    ticks: customTicks,
                    tickFormatter: type === "percent" ? valueToPercent : valueFormatter,
                    allowDecimals: allowDecimals,
                  }
                : {
                    dataKey: index,
                    ticks: startEndOnly
                      ? [data[0][index], data[data.length - 1][index]]
                      : undefined,
                    type: "category",
                    interval: "equidistantPreserveStart",
                  })}
            >
              {yAxisLabel && (
                <Label
                  position="insideLeft"
                  style={{ textAnchor: "middle" }}
                  angle={-90}
                  offset={-15}
                  className="fill-gray-800 text-sm font-medium dark:fill-gray-200"
                >
                  {yAxisLabel}
                </Label>
              )}
            </YAxis>
            <Tooltip
              wrapperStyle={{ outline: "none", zIndex: 50 }}
              isAnimationActive={true}
              animationDuration={100}
              cursor={{ fill: "#d1d5db", opacity: "0.15" }}
              offset={20}
              shared={sharedTooltip}
              position={{
                y: layout === "horizontal" ? 0 : undefined,
                x: layout === "horizontal" ? undefined : yAxisWidth + 20,
              }}
              content={({ active, payload, label }) => {
                const cleanPayload: TooltipProps["payload"] = payload
                  ? payload.map((item: unknown) => {
                      const payloadItem = item as RechartsTooltipPayloadItem;
                      const dataKey = payloadItem.dataKey;
                      const category =
                        typeof dataKey === "string" ? dataKey : String(dataKey ?? "");
                      const value = payloadItem.value ?? 0;
                      const payloadData = payloadItem.payload ?? {};
                      const indexValue = payloadData[index];
                      return {
                        category,
                        value,
                        index:
                          typeof indexValue === "string" || typeof indexValue === "number"
                            ? String(indexValue)
                            : "",
                        color: categoryColors.get(category) as AvailableChartColorsKeys,
                        type: payloadItem.type,
                        payload: payloadData,
                      };
                    })
                  : [];

                const labelString =
                  typeof label === "string" ? label : String(label ?? "");

                if (
                  tooltipCallback &&
                  (active !== prevActiveRef.current ||
                    labelString !== prevLabelRef.current)
                ) {
                  tooltipCallback({ active, payload: cleanPayload, label: labelString });
                  prevActiveRef.current = active;
                  prevLabelRef.current = labelString;
                }

                return showTooltip && active ? (
                  CustomTooltip ? (
                    <CustomTooltip
                      active={active}
                      payload={cleanPayload}
                      label={labelString}
                    />
                  ) : (
                    <ChartTooltip
                      active={active}
                      payload={cleanPayload}
                      label={labelString}
                      valueFormatter={valueFormatter}
                    />
                  )
                ) : null;
              }}
            />
            {showLegend ? (
              <RechartsLegend
                verticalAlign="top"
                height={legendHeight}
                content={({ payload }) =>
                  ChartLegend(
                    { payload },
                    categoryColors,
                    setLegendHeight,
                    undefined, // No active legend highlighting
                    hasOnValueChange
                      ? (clickedLegendItem: string) => onCategoryClick(clickedLegendItem)
                      : undefined,
                    enableLegendSlider,
                    legendPosition,
                    yAxisWidth,
                  )
                }
              />
            ) : null}
            {categories.map((category) => (
              <Bar
                className={cn(
                  getColorClassName(
                    categoryColors.get(category) as AvailableChartColorsKeys,
                    "fill",
                  ),
                  onValueChange ? "cursor-pointer" : "",
                )}
                key={category}
                name={category}
                type="linear"
                dataKey={category}
                stackId={stacked ? "stack" : undefined}
                isAnimationActive={false}
                fill=""
                shape={(props: any) =>
                  renderShape(props as RechartsBarShapeProps, layout)
                }
                onClick={onBarClick}
              />
            ))}
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    );
  },
);

BarChart.displayName = "BarChart";

export { BarChart, type BarChartEventProps, type TooltipProps };
